import { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { getChoresClient } from "sj-ui-toolkit/src/client/svcClients";

interface PlaArchiveDialogProps {
  isVisible: boolean;
  assignmentUuid?: string;
  learnerName?: string;
  onComplete: (success: boolean) => void;
}

export default function PlaArchiveDialog({
  isVisible,
  assignmentUuid,
  learnerName,
  onComplete,
}: PlaArchiveDialogProps) {
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // The dialog stays mounted across open/close cycles, so clear any prior error
  // when it opens — otherwise a failed archive for one learner would still show
  // when the dialog is reopened for another.
  useEffect(() => {
    if (isVisible) setArchiveError(null);
  }, [isVisible]);

  const onConfirm = async () => {
    if (!assignmentUuid) {
      setArchiveError(
        "This assignment cannot be archived because its underlying assignment id is missing."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await getChoresClient().archive(assignmentUuid);
      if (error) throw error;

      setArchiveError(null);
      onComplete(true);
    } catch (error) {
      console.error(error);
      setArchiveError("There was an error archiving this assignment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      visible={isVisible}
      onHide={() => onComplete(false)}
      header="Archive Assignment"
      maskStyle={{ backdropFilter: "blur(5px)" }}
      footer={
        <div className="flex gap-2">
          <Button
            label="Cancel"
            onClick={() => onComplete(false)}
            severity="secondary"
            disabled={isSubmitting}
          />
          <Button label="Confirm" onClick={() => void onConfirm()} loading={isSubmitting} />
        </div>
      }
    >
      <p className="p-1">
        Are you sure you want to archive the personalized lesson assignment for{" "}
        <strong>{learnerName}</strong>?
      </p>
      <p className="p-1">
        This will remove it from the learner&apos;s home page, but will not delete any progress
        data.
      </p>
      {archiveError && <p className="error">{archiveError}</p>}
    </Dialog>
  );
}
